import {
  AlertCircle,
  ArrowLeft,
  Crown,
  Gift,
  Heart,
  MessageCircle,
  Sparkles,
  Star,
  Users,
  Zap,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React, { useState } from "react";

import { trackEngagement, trackFormAnalytics } from "../utils/analytics";
import {
  validateEmail,
  validateName,
  validateRequired,
} from "../utils/validation";

import { TierSelectionCard, WaitlistTier } from "./TierSelectionCard";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { FormField } from "./ui/form-field";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

export interface WaitlistData {
  email: string;
  name: string;
  relationshipType: string;
  primaryGoal: string;
  currentChallenges: string[];
  communicationFrequency: string;
  discoverySource: string;
  betaInterest: boolean;
  marketingConsent: boolean;
  additionalThoughts: string;
  selectedTier: WaitlistTier;
}

interface WaitlistScreenProps {
  onSubmit: (
    data: WaitlistData,
    step: number,
    isFinalStep: boolean
  ) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
}

export default function WaitlistScreen({
  onSubmit,
  onBack,
  isLoading,
}: WaitlistScreenProps) {
  const [formData, setFormData] = useState<WaitlistData>({
    email: "",
    name: "",
    relationshipType: "",
    primaryGoal: "",
    currentChallenges: [],
    communicationFrequency: "",
    discoverySource: "",
    betaInterest: false,
    marketingConsent: true,
    additionalThoughts: "",
    selectedTier: "free",
  });

  const [errors, setErrors] = useState<Partial<WaitlistData>>({});
  const [submitError, setSubmitError] = useState<string>("");
  const [step, setStep] = useState(1);
  const [valid, setValid] = useState<
    Partial<Record<keyof WaitlistData, boolean>>
  >({});

  /**
   * Validates a single field and returns error message if invalid
   */
  const validateField = (
    field: keyof WaitlistData,
    value: WaitlistData[keyof WaitlistData]
  ): string | undefined => {
    if (field === "email") {
      return validateEmail(value as string);
    }
    if (field === "name") {
      return validateName(value as string);
    }
    if (field === "relationshipType") {
      return validateRequired(value as string, "Relationship type");
    }
    if (field === "primaryGoal") {
      return validateRequired(value as string, "Primary goal");
    }
    if (field === "currentChallenges") {
      return validateRequired(value as string[], "Challenge");
    }
    return undefined;
  };

  /**
   * Handle field blur - validates field and shows error/success state
   */
  const handleBlur = (field: keyof WaitlistData) => {
    const value = formData[field];
    const error = validateField(field, value);

    // Update error state
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
      setValid(prev => ({ ...prev, [field]: false }));
    } else {
      setErrors(prev => ({ ...prev, [field]: undefined }));
      setValid(prev => ({ ...prev, [field]: true }));
    }

    // Track blur event
    trackFormAnalytics("blur", "waitlist_form", field);
  };

  const handleInputChange = (
    field: keyof WaitlistData,
    value: WaitlistData[keyof WaitlistData]
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }

    // Track form interaction
    trackFormAnalytics("input", "waitlist_form", field);
  };

  const handleChallengeToggle = (challenge: string) => {
    const currentChallenges = formData.currentChallenges;
    if (currentChallenges.includes(challenge)) {
      handleInputChange(
        "currentChallenges",
        currentChallenges.filter(c => c !== challenge)
      );
    } else {
      handleInputChange("currentChallenges", [...currentChallenges, challenge]);
    }
  };

  const validateStep = (stepNumber: number) => {
    const newErrors: Partial<WaitlistData> = {};

    if (stepNumber === 1) {
      const emailError = validateField("email", formData.email);
      if (emailError) newErrors.email = emailError;

      const nameError = validateField("name", formData.name);
      if (nameError) newErrors.name = nameError;

      const relationshipError = validateField(
        "relationshipType",
        formData.relationshipType
      );
      if (relationshipError) newErrors.relationshipType = relationshipError;
    }

    if (stepNumber === 2) {
      const goalError = validateField("primaryGoal", formData.primaryGoal);
      if (goalError) newErrors.primaryGoal = goalError;

      const challengesError = validateField(
        "currentChallenges",
        formData.currentChallenges
      );
      if (challengesError)
        newErrors.currentChallenges = [challengesError] as unknown as string[];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (validateStep(step)) {
      try {
        // Submit current step data
        await onSubmit(formData, step, false);

        // Move to next step
        setStep(step + 1);

        // Track step progression
        trackEngagement("click", "next_step", {
          page: "waitlist",
          current_step: step,
          next_step: step + 1,
        });
      } catch (error) {
        console.error("Failed to save step:", error);
        setSubmitError("Failed to save progress. Please try again.");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    // Track form submission attempt
    trackFormAnalytics("submit_attempt", "waitlist_form");

    if (validateStep(2) && validateStep(3)) {
      try {
        // Submit as final step
        await onSubmit(formData, 3, true);

        // Track successful submission
        trackFormAnalytics("submit_success", "waitlist_form");
      } catch (error) {
        console.error("Waitlist submission failed:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to join waitlist. Please try again.";
        setSubmitError(errorMessage);

        // Track submission error
        trackFormAnalytics("validation_error", "waitlist_form", undefined);
      }
    } else {
      // Track validation errors
      trackFormAnalytics("validation_error", "waitlist_form", undefined);
    }
  };

  const relationshipTypes = [
    { value: "romantic_couple", label: "Romantic Couple", icon: Heart },
    { value: "married_couple", label: "Married Couple", icon: Heart },
    { value: "co_parents", label: "Co-Parents", icon: Users },
    { value: "close_friends", label: "Close Friends", icon: Users },
    { value: "family_members", label: "Family Members", icon: Users },
    { value: "other", label: "Other", icon: MessageCircle },
  ];

  const primaryGoals = [
    "Improve communication during conflicts",
    "Deepen emotional intimacy",
    "Better co-parenting coordination",
    "Strengthen friendship bonds",
    "Develop healthier conversation patterns",
    "Learn to express needs more clearly",
    "Build better listening skills",
    "Navigate difficult conversations",
    "Other",
  ];

  const challenges = [
    "We often misunderstand each other",
    "Conversations escalate into arguments",
    "We avoid difficult topics",
    "Someone feels unheard or dismissed",
    "We struggle with emotional conversations",
    "Scheduling quality conversation time",
    "Different communication styles",
    "Past conflicts interfere with present talks",
    "Technology gets in the way of real connection",
  ];

  const frequencies = [
    { value: "daily", label: "Daily" },
    { value: "few_times_week", label: "A few times a week" },
    { value: "weekly", label: "Weekly" },
    { value: "few_times_month", label: "A few times a month" },
    { value: "rarely", label: "Rarely" },
  ];

  const discoverySources = [
    "Social media",
    "Search engine",
    "Friend or family recommendation",
    "Online article or blog",
    "Podcast",
    "App store browsing",
    "Other",
  ];

  return (
    <div className="min-h-screen bg-background text-foreground p-4 overflow-y-auto">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center mb-6"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              trackEngagement("click", "back_button", {
                page: "waitlist",
                current_step: step,
              });
              onBack();
            }}
            className="p-2"
            disabled={isLoading}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="ml-4">
            <h1 className="text-xl font-semibold">Join the Koru Waitlist</h1>
            <p className="text-sm text-muted-foreground">Step {step} of 3</p>
          </div>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          className="w-full bg-muted rounded-full h-1 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="bg-primary h-1 rounded-full"
            initial={{ width: "33%" }}
            animate={{ width: `${(step / 3) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>

        {/* Incentives Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="mb-6 glass-primary">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Gift className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm mb-2 flex items-center">
                    <Crown className="h-4 w-4 text-primary mr-1" />
                    Exclusive Waitlist Benefits
                  </h3>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center">
                      <Sparkles className="h-3 w-3 text-secondary mr-2" />
                      <span>Early beta access with friends & family</span>
                    </div>
                    <div className="flex items-center">
                      <Star className="h-3 w-3 text-accent mr-2" />
                      <span>Founding Member status available ($5)</span>
                    </div>
                    <div className="flex items-center">
                      <Zap className="h-3 w-3 text-primary mr-2" />
                      <span>Priority access option ($1)</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <form onSubmit={handleSubmit} className="waitlist-form space-y-6">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Let&apos;s get started
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Tell us about yourself and your relationship
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    htmlFor="name"
                    label="Your Name"
                    error={errors.name}
                    valid={valid.name}
                  >
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={e => handleInputChange("name", e.target.value)}
                      onFocus={() =>
                        trackFormAnalytics("focus", "waitlist_form", "name")
                      }
                      onBlur={() => handleBlur("name")}
                      placeholder="Enter your name"
                      className={`bg-white text-black border-2 border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                        errors.name
                          ? "input-error"
                          : valid.name
                            ? "input-valid"
                            : ""
                      }`}
                    />
                  </FormField>

                  <FormField
                    htmlFor="email"
                    label="Email Address"
                    error={errors.email}
                    valid={valid.email}
                  >
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={e => handleInputChange("email", e.target.value)}
                      onFocus={() =>
                        trackFormAnalytics("focus", "waitlist_form", "email")
                      }
                      onBlur={() => handleBlur("email")}
                      placeholder="your@email.com"
                      className={`bg-white text-black border-2 border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                        errors.email
                          ? "input-error"
                          : valid.email
                            ? "input-valid"
                            : ""
                      }`}
                    />
                  </FormField>

                  <div>
                    <Label>What describes your relationship best?</Label>
                    <RadioGroup
                      value={formData.relationshipType}
                      onValueChange={value =>
                        handleInputChange("relationshipType", value)
                      }
                      className="mt-2"
                    >
                      {relationshipTypes.map(({ value, label, icon: Icon }) => (
                        <div
                          key={value}
                          className="flex items-center space-x-3"
                        >
                          <RadioGroupItem
                            value={value}
                            id={value}
                            className="bg-white border-2 border-gray-300 text-primary data-[state=checked]:bg-white data-[state=checked]:border-primary shadow-sm"
                            style={{
                              backgroundColor: "white",
                              borderColor:
                                formData.relationshipType === value
                                  ? "var(--primary)"
                                  : "#d1d5db",
                            }}
                          />
                          <Label
                            htmlFor={value}
                            className="flex items-center cursor-pointer"
                          >
                            <Icon className="h-4 w-4 mr-2 text-muted-foreground" />
                            {label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    {errors.relationshipType && (
                      <p className="text-xs text-destructive mt-1">
                        {errors.relationshipType}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Button
                type="button"
                onClick={handleNext}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={isLoading}
              >
                Continue
              </Button>
            </motion.div>
          )}

          {/* Step 2: Goals & Challenges */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Your Communication Goals
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Help us understand what you&apos;re hoping to improve
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>
                      What&apos;s your primary goal for better communication?
                    </Label>
                    <Select
                      value={formData.primaryGoal}
                      onValueChange={value =>
                        handleInputChange("primaryGoal", value)
                      }
                    >
                      <SelectTrigger
                        className={`bg-white text-black border-2 border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                          errors.primaryGoal ? "border-destructive" : ""
                        }`}
                      >
                        <SelectValue placeholder="Select your primary goal" />
                      </SelectTrigger>
                      <SelectContent className="bg-white text-black">
                        {primaryGoals.map(goal => (
                          <SelectItem key={goal} value={goal}>
                            {goal}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <AnimatePresence mode="wait">
                      {errors.primaryGoal && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-xs text-destructive mt-1 flex items-center gap-1"
                          role="alert"
                        >
                          <AlertCircle className="h-3 w-3" />
                          {errors.primaryGoal}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  <div>
                    <Label>
                      What communication challenges do you face? (Select all
                      that apply)
                    </Label>
                    <div className="mt-2 space-y-3">
                      {challenges.map(challenge => (
                        <div
                          key={challenge}
                          className="flex items-start space-x-3"
                        >
                          <Checkbox
                            id={challenge}
                            checked={formData.currentChallenges.includes(
                              challenge
                            )}
                            onCheckedChange={() =>
                              handleChallengeToggle(challenge)
                            }
                            className="mt-0.5 bg-white border-2 border-gray-300 rounded-sm data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-black shadow-sm"
                            style={{
                              backgroundColor:
                                formData.currentChallenges.includes(challenge)
                                  ? "var(--primary)"
                                  : "white",
                              borderColor: formData.currentChallenges.includes(
                                challenge
                              )
                                ? "var(--primary)"
                                : "#d1d5db",
                            }}
                          />
                          <Label
                            htmlFor={challenge}
                            className="text-sm cursor-pointer leading-5 flex-1"
                          >
                            {challenge}
                          </Label>
                        </div>
                      ))}
                    </div>
                    <AnimatePresence mode="wait">
                      {errors.currentChallenges && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-xs text-destructive mt-1 flex items-center gap-1"
                          role="alert"
                        >
                          <AlertCircle className="h-3 w-3" />
                          {errors.currentChallenges[0]}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </CardContent>
              </Card>

              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={isLoading}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Additional Details */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Almost done!</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Just a few more details to help us serve you better
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>
                      How often do you have meaningful conversations?
                    </Label>
                    <Select
                      value={formData.communicationFrequency}
                      onValueChange={value =>
                        handleInputChange("communicationFrequency", value)
                      }
                    >
                      <SelectTrigger className="bg-white text-black border-2 border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent className="bg-white text-black">
                        {frequencies.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>How did you hear about Koru?</Label>
                    <Select
                      value={formData.discoverySource}
                      onValueChange={value =>
                        handleInputChange("discoverySource", value)
                      }
                    >
                      <SelectTrigger className="bg-white text-black border-2 border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent className="bg-white text-black">
                        {discoverySources.map(source => (
                          <SelectItem key={source} value={source}>
                            {source}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="thoughts">
                      Any additional thoughts or specific features you&apos;d
                      like to see? (Optional)
                    </Label>
                    <Textarea
                      id="thoughts"
                      value={formData.additionalThoughts}
                      onChange={e =>
                        handleInputChange("additionalThoughts", e.target.value)
                      }
                      placeholder="Tell us what would make Koru perfect for you..."
                      className="mt-1 bg-white text-black border-2 border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="beta"
                        checked={formData.betaInterest}
                        onCheckedChange={checked =>
                          handleInputChange("betaInterest", checked)
                        }
                        className="mt-0.5 bg-white border-2 border-gray-300 rounded-sm data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-black shadow-sm"
                        style={{
                          backgroundColor: formData.betaInterest
                            ? "var(--primary)"
                            : "white",
                          borderColor: formData.betaInterest
                            ? "var(--primary)"
                            : "#d1d5db",
                        }}
                      />
                      <Label
                        htmlFor="beta"
                        className="text-sm leading-5 cursor-pointer"
                      >
                        I&apos;m interested in beta testing Koru with my
                        partner/group
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="marketing"
                        checked={formData.marketingConsent}
                        onCheckedChange={checked =>
                          handleInputChange("marketingConsent", checked)
                        }
                        className="mt-0.5 bg-white border-2 border-gray-300 rounded-sm data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-black shadow-sm"
                        style={{
                          backgroundColor: formData.marketingConsent
                            ? "var(--primary)"
                            : "white",
                          borderColor: formData.marketingConsent
                            ? "var(--primary)"
                            : "#d1d5db",
                        }}
                      />
                      <Label
                        htmlFor="marketing"
                        className="text-sm leading-5 cursor-pointer"
                      >
                        Yes, I&apos;d like to receive special offers and launch
                        updates from Koru
                      </Label>
                    </div>
                  </div>

                  {/* Tier Selection */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <TierSelectionCard
                      selectedTier={formData.selectedTier}
                      onTierSelect={tier =>
                        handleInputChange("selectedTier", tier)
                      }
                      disabled={isLoading}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1"
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full"
                    />
                  ) : (
                    "Join Waitlist"
                  )}
                </Button>
              </div>

              {/* Submit Error Alert */}
              {submitError && (
                <Alert className="border-destructive/20 bg-destructive/10 mt-4">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <AlertDescription className="text-destructive">
                    {submitError}
                  </AlertDescription>
                </Alert>
              )}
            </motion.div>
          )}
        </form>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <p className="text-xs text-muted-foreground">
            Your responses help us build Koru specifically for couples and close
            relationships like yours.
          </p>
          <Badge variant="outline" className="mt-2 text-xs">
            <Sparkles className="h-3 w-3 mr-1" />
            Premium AI-Assisted Communication
          </Badge>
        </motion.div>
      </div>
    </div>
  );
}
